import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft } from 'lucide-react';

/**
 * Catches any render/effect error below it and shows a graceful fallback
 * instead of the blank-screen-of-death you get from an unhandled React error.
 *
 * Use around routes that can explode when the backend returns an unexpected
 * shape (e.g. household pages when a Gratuit post-trial user tries to edit).
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught render error:', error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const fallback = this.props.fallback;
    if (fallback) {
      return typeof fallback === 'function'
        ? fallback({ error: this.state.error, reset: this.handleReset })
        : fallback;
    }

    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-amber-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-3">Un pépin sur cette page</h1>
          <p className="text-sm text-zinc-400 mb-8 leading-relaxed">
            Cette section n'a pas pu se charger correctement. Tes données sont en sécurité — c'est
            juste l'affichage qui a flanché.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={this.handleReset}
              className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black text-sm font-semibold transition-colors"
            >
              Réessayer
            </button>
            <Link
              to="/dashboard"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-medium border border-zinc-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour au dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
